import { Controller } from "@hotwired/stimulus";

/*
 * An autocomplete dropdown backed by a native <datalist>.
 *
 * Why not the native `list` attribute? Browser rendering is inconsistent
 * and unstyable. Why not Hotwire Combobox? It expects an AJAX endpoint
 * for suggestions. Here the options already live in a <datalist> that
 * Turbo keeps fresh as the user filters — no extra round-trip needed.
 *
 * Usage:
 *
 *   <!-- Inside a Turbo Frame so options stay fresh: -->
 *   <datalist id="brand_names">
 *     <option value="Carrier">
 *     <option value="Trane">
 *   </datalist>
 *
 *   <div data-controller="datalist" data-datalist-list-value="brand_names">
 *     <input data-datalist-target="input"
 *            data-action="focus->datalist#suggest
 *                         input->datalist#suggest
 *                         keydown->datalist#navigate">
 *     <ul data-datalist-target="menu" class="hidden"></ul>
 *   </div>
 *
 * Set data-datalist-prefix-value="true" to match from the start of a value
 * rather than anywhere in it.
 *
 * Give an option a label to list it by that name, with its value previewed
 * beneath. Either one matches, and picking it fills in the value.
 *
 * Set data-datalist-above-value="true" to open the menu above the input, for
 * an input near the bottom of the viewport.
 */

export default class extends Controller {
  static targets = ["input", "menu"];
  static values = { list: String, prefix: Boolean, above: Boolean };

  connect() {
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleOutsideFocus = this.handleOutsideFocus.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    document.addEventListener("click", this.handleOutsideClick);
    document.addEventListener("focusin", this.handleOutsideFocus);
    // Capture, since scroll doesn't bubble and the input may be in a scrolling container.
    document.addEventListener("scroll", this.handleScroll, true);
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener("focusin", this.handleOutsideFocus);
    document.removeEventListener("scroll", this.handleScroll, true);
  }

  suggest() {
    if (document.activeElement !== this.inputTarget) return;
    const query = this.inputTarget.value.trim().toLowerCase();
    const options = this.datalistOptions();
    const matches = query ? options.filter((o) => this.matches(o, query)) : options;

    // Nothing left to suggest once the input holds the only match, as it
    // does right after a pick.
    if (
      matches.length === 0 ||
      (matches.length === 1 && matches[0].value.toLowerCase() === query)
    ) {
      this.close();
      return;
    }

    const fragment = document.createDocumentFragment();
    matches.forEach((o) => {
      const li = document.createElement("li");
      li.className = "px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm";
      // Keeping focus in the input means it isn't left with a half-typed value.
      li.dataset.action = "mousedown->datalist#preventBlur click->datalist#select";
      li.dataset.value = o.value;
      if (o.label) {
        li.append(this.line(o.label, "font-medium"), this.line(o.value, "text-secondary truncate"));
      } else {
        li.textContent = o.value;
      }
      fragment.appendChild(li);
    });
    this.menuTarget.replaceChildren(fragment);

    const opening = this.menuTarget.classList.contains("hidden");
    // Out of the layout before it shows, so it can't move the input it's
    // placed against. Above an input at the bottom of a column, it would.
    this.menuTarget.style.position = "fixed";
    this.menuTarget.classList.remove("hidden");
    if (opening) this.makeRoom();
    this.positionMenu();
    this.activeIndex = -1;
  }

  matches(option, query) {
    return [option.label, option.value].some((text) => {
      const candidate = text?.toLowerCase();
      if (!candidate) return false;
      return this.prefixValue ? candidate.startsWith(query) : candidate.includes(query);
    });
  }

  line(text, className) {
    const div = document.createElement("div");
    div.className = className;
    div.textContent = text;
    return div;
  }

  // On one side of the input, so it stays where the eye is, and no taller
  // than the room left there.
  positionMenu() {
    const rect = this.inputTarget.getBoundingClientRect();
    const side = this.aboveValue
      ? {
          top: "auto",
          bottom: `${window.innerHeight - rect.top + 4}px`,
          maxHeight: `${Math.min(this.tallest, rect.top - 20)}px`,
        }
      : {
          top: `${rect.bottom + 4}px`,
          bottom: "auto",
          maxHeight: `${Math.min(this.tallest, window.innerHeight - rect.bottom - 20)}px`,
        };

    Object.assign(this.menuTarget.style, {
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      ...side,
    });
  }

  // Scrolls once, as the menu opens, so that it has room at its tallest and
  // doesn't move as the matches narrow.
  makeRoom() {
    this.inputTarget.style[this.aboveValue ? "scrollMarginTop" : "scrollMarginBottom"] =
      `${this.tallest + 4}px`;
    this.inputTarget.scrollIntoView({ block: "nearest" });
  }

  // The menu's own max height, read before positionMenu overrides it.
  get tallest() {
    this._tallest ??=
      parseFloat(getComputedStyle(this.menuTarget).maxHeight) || window.innerHeight / 2;
    return this._tallest;
  }

  preventBlur(event) {
    event.preventDefault();
  }

  select(event) {
    this.inputTarget.value = event.currentTarget.dataset.value;
    this.close();
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  navigate(event) {
    if (!["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(event.key)) return;
    if (this.menuTarget.classList.contains("hidden")) return;

    const items = this.menuTarget.querySelectorAll("li");
    if (items.length === 0) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === "Enter") {
      // With nothing highlighted, Enter is left to the input, as when the menu
      // is closed.
      if (this.activeIndex < 0) return;

      event.preventDefault();
      items[this.activeIndex].click();
      return;
    }

    event.preventDefault();
    if (event.key === "ArrowDown") {
      this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
    } else {
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
    }

    items.forEach((item, i) => item.classList.toggle("bg-gray-100", i === this.activeIndex));
  }

  close() {
    this.menuTarget.classList.add("hidden");
    this.menuTarget.innerHTML = "";
    this.activeIndex = -1;
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close();
  }

  handleOutsideFocus(event) {
    if (!this.element.contains(event.target)) this.close();
  }

  handleScroll(event) {
    if (this.menuTarget.contains(event.target)) return;
    if (!this.menuTarget.classList.contains("hidden")) this.positionMenu();
  }

  datalistOptions() {
    const list = document.getElementById(this.listValue);
    if (!list) return [];
    return Array.from(list.options)
      .map((o) => ({ value: o.value, label: o.getAttribute("label") }))
      .filter((o) => o.value);
  }
}
